// src/components/reports/ReportTable.js
import { Fragment, useState } from 'react';
import Modal from '../common/Modal';
import { Logo } from '../../hooks/useLogo';
import settingsService from '../../services/settingsService';
import { getAttendanceDurationMinutes, formatAttendanceTime } from '../../utils/dateUtils';

const ReportTable = ({ reportType, data, onExport }) => {
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showSalesAgentModal, setShowSalesAgentModal] = useState(false);
    const [selectedSalesAgent, setSelectedSalesAgent] = useState(null);
    const [agentDetails, setAgentDetails] = useState(null);
    const [agentDetailsLoading, setAgentDetailsLoading] = useState(false);

    // Group attendance by staff and date (same logic as AttendanceTable)
    const groupAttendanceByStaffAndDate = (records) => {
        const grouped = {};

        records.forEach(record => {
            // Group strictly by staff and day so all logs for the same day roll into one record.
            const key = `${record.user_id || 'nouser'}_${record.date || 'nodate'}`;
            if (!grouped[key]) {
                grouped[key] = {
                    ...record,
                    entries: []
                };
            }
            grouped[key].entries.push({
                sign_in_time: record.sign_in_time,
                sign_out_time: record.sign_out_time,
                attendance_type: record.attendance_type,
                id: record.id
            });
        });

        return Object.values(grouped);
    };

    const groupedAttendance = reportType === 'attendance' ? groupAttendanceByStaffAndDate(data) : data;
    const tableTitle = reportType === 'sales-agents' ? 'Sales Agent Report Data' : 'Attendance Report Data';
    const salesAgentSummary = reportType === 'sales-agents'
        ? (() => {
            const topPerformer = groupedAttendance.reduce((top, current) => {
                const currentCollected = Number(current.total_collected || 0);
                const topCollected = Number(top?.total_collected || 0);
                return currentCollected > topCollected ? current : top;
            }, null);

            const totalOrgs         = groupedAttendance.reduce((s, i) => s + Number(i.organizations_assigned || 0), 0);
            const totalCollectedPkr = groupedAttendance.reduce((s, i) => s + Number(i.total_collected_pkr || 0), 0);
            const totalCollectedUsd = groupedAttendance.reduce((s, i) => s + Number(i.total_collected_usd || 0), 0);
            const totalCommPkr      = groupedAttendance.reduce((s, i) => s + Number(i.commission_pkr || 0), 0);
            const totalCommUsd      = groupedAttendance.reduce((s, i) => s + Number(i.commission_usd || 0), 0);
            const totalPayments     = groupedAttendance.reduce((s, i) => s + Number(i.completed_payments || 0), 0);

            return {
                topPerformerName: topPerformer?.sales_agent_name || topPerformer?.name || '-',
                topPerformerPkr: Number(topPerformer?.total_collected_pkr || 0),
                topPerformerUsd: Number(topPerformer?.total_collected_usd || 0),
                totalOrgs,
                totalCollectedPkr,
                totalCollectedUsd,
                totalCommPkr,
                totalCommUsd,
                totalPayments
            };
        })()
        : null;

    // Pagination
    const totalPages = Math.ceil(groupedAttendance.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = groupedAttendance.slice(startIndex, endIndex);

    const handlePrintFullReport = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) { alert('Please allow popups to print this report'); return; }

        const appLogo = localStorage.getItem('app_logo') || '';
        const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
        const logoUrl = (() => {
            const v = String(appLogo || '').trim();
            if (!v) return '';
            if (/^https?:\/\//i.test(v) || v.startsWith('data:image/')) return v;
            return `${apiBase}${v.startsWith('/') ? '' : '/'}${v}`;
        })();
        const esc = v => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

        const fmtTime = formatAttendanceTime;
        const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '-';
        const calcHours = (i, o) => {
            const m = getAttendanceDurationMinutes(i, o);
            if (m === null) return 'Invalid';
            return `${Math.floor(m/60)}h ${m%60}m`;
        };

        const isAttendance = reportType === 'attendance';

        const rows = groupedAttendance.map((record, idx) => {
            if (!isAttendance) {
                return `<tr>
                    <td>${idx+1}</td>
                    <td>${esc(record.sales_agent_name || record.name || '-')}</td>
                    <td>${esc(record.cell_number || '-')}</td>
                    <td>${esc([record.city,record.country].filter(Boolean).join(', ') || '-')}</td>
                    <td>${esc(record.organizations_assigned ?? 0)}</td>
                    <td>${esc(record.completed_payments ?? 0)}</td>
                    <td>PKR ${esc(Number(record.total_collected_pkr||0).toLocaleString(undefined,{maximumFractionDigits:0}))}</td>
                    <td>USD ${esc(Number(record.total_collected_usd||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}))}</td>
                    <td>${esc(record.commission_display || '-')}</td>
                    <td>PKR ${esc(Number(record.commission_pkr||0).toLocaleString(undefined,{maximumFractionDigits:0}))}</td>
                    <td>USD ${esc(Number(record.commission_usd||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}))}</td>
                </tr>`;
            }
            const entries = record.entries || [];
            const firstIn  = entries.reduce((t, e) => (!t || (e.sign_in_time && e.sign_in_time < t)) ? e.sign_in_time : t, null);
            const lastOut  = entries.reduce((t, e) => (!t || (e.sign_out_time && e.sign_out_time > t)) ? e.sign_out_time : t, null);
            let totalMin = 0;
            entries.forEach(e => {
                const m = getAttendanceDurationMinutes(e.sign_in_time, e.sign_out_time);
                if (m !== null) totalMin += m;
            });
            const hrs = totalMin > 0 ? `${Math.floor(totalMin/60)}h ${totalMin%60}m` : '-';
            const st = (record.actual_status || record.status || '').toLowerCase();
            const statusText = st === 'holiday' ? 'Holiday' : st === 'late' ? 'Late' : st === 'on_leave' ? 'On Leave' : st === 'absent' || (!firstIn && !lastOut) ? 'Absent' : 'Present';
            return `<tr>
                <td>${esc(record.staff_name || record.username || '-')}</td>
                <td>${esc(record.designation || '-')}</td>
                <td>${fmtDate(record.date)}</td>
                <td>${esc(record.premises_name || '-')}</td>
                <td>${esc(record.shift_name || '-')}</td>
                <td>${fmtTime(firstIn)}</td>
                <td>${fmtTime(lastOut)}</td>
                <td>${hrs}</td>
                <td>${statusText}</td>
            </tr>`;
        }).join('');

        const thead = isAttendance
            ? '<tr><th>Staff</th><th>Designation</th><th>Date</th><th>Premises</th><th>Shift</th><th>Time In</th><th>Time Out</th><th>Hours</th><th>Status</th></tr>'
            : '<tr><th>#</th><th>Agent</th><th>Phone</th><th>Location</th><th>Orgs</th><th>Payments</th><th>Collected (PKR)</th><th>Collected (USD)</th><th>Comm. Rate</th><th>Comm. (PKR)</th><th>Comm. (USD)</th></tr>';

        printWindow.document.write(`<!DOCTYPE html><html><head>
            <title>${isAttendance ? 'Attendance' : 'Sales Agent'} Report</title>
            <style>
                @page { size: A4 landscape; margin: 10mm; }
                body { font-family: Arial, sans-serif; font-size: 11px; color: #1f2937; margin: 0; padding: 8px; }
                .hdr { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
                .logo-img { height:44px; max-width:200px; object-fit:contain; }
                .logo-fallback { font-size:20px; font-weight:800; color:#0f172a; }
                .meta { text-align:right; }
                .meta h1 { margin:0; font-size:18px; font-weight:700; color:#0f172a; }
                .meta p { margin:3px 0 0; font-size:11px; color:#64748b; }
                hr { border:none; border-top:2px solid #e2e8f0; margin:6px 0 10px; }
                table { width:100%; border-collapse:collapse; table-layout:fixed; }
                th, td { border:1px solid #e5e7eb; padding:5px 6px; text-align:left; word-wrap:break-word; }
                th { background:#f1f5f9; font-weight:700; color:#1e293b; font-size:10px; text-transform:uppercase; }
                tr:nth-child(even) td { background:#f9fafb; }
                .footer { margin-top:8px; font-size:10px; color:#94a3b8; text-align:right; }
            </style>
        </head><body>
            <div class="hdr">
                <div>
                    ${logoUrl ? `<img class="logo-img" src="${esc(logoUrl)}" alt="Logo" onerror="this.style.display='none'" />` : '<div class="logo-fallback">eHAAZRI</div>'}
                </div>
                <div class="meta">
                    <h1>${isAttendance ? 'Attendance Report' : 'Sales Agent Report'}</h1>
                    <p>Generated: ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })} &nbsp;·&nbsp; ${groupedAttendance.length} records</p>
                </div>
            </div>
            <hr />
            <table><thead>${thead}</thead><tbody>${rows}</tbody></table>
            <div class="footer">eHAAZRI Attendance Management System</div>
        </body></html>`);
        printWindow.document.close();
        printWindow.focus();
        const safePrint = () => setTimeout(() => { printWindow.print(); printWindow.close(); }, 200);
        printWindow.onload = () => {
            const img = printWindow.document.querySelector('.logo-img');
            if (!img) return safePrint();
            if (img.complete && img.naturalWidth > 0) return safePrint();
            let done = false;
            const fin = () => { if (done) return; done = true; safePrint(); };
            img.onload = fin; img.onerror = fin;
            setTimeout(fin, 1200);
        };
    };

    const handlePrintModal = () => {
        // Get the modal content
        const printContent = document.getElementById('attendance-details-print');
        if (!printContent) return;

        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to print this report');
            return;
        }

        // Write the content with styles
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Attendance Report - ${selectedRecord?.staff_name || 'Staff'}</title>
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                        padding: 20px;
                        color: #2d3748;
                    }
                    .modal-actions {
                        display: none !important;
                    }
                    .detail-header {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        padding: 1.5rem;
                        border-radius: 12px;
                        color: white;
                        margin-bottom: 1.5rem;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .detail-header h3 {
                        margin: 0 0 0.8rem 0;
                        font-size: 1.5rem;
                    }
                    .detail-header p {
                        margin: 0.4rem 0;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        font-size: 0.95rem;
                    }
                    .summary-stats {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 1rem;
                        margin-bottom: 1.5rem;
                    }
                    .summary-card {
                        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                        border-radius: 12px;
                        padding: 1.25rem;
                        display: flex;
                        align-items: center;
                        gap: 1rem;
                        border-left: 4px solid #667eea;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        page-break-inside: avoid;
                    }
                    .summary-card.yearly {
                        border-left-color: #764ba2;
                    }
                    .summary-icon {
                        width: 50px;
                        height: 50px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 1.5rem;
                        flex-shrink: 0;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .summary-info h5 {
                        margin: 0 0 0.3rem 0;
                        color: #2d3748;
                        font-size: 0.9rem;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .summary-period {
                        margin: 0 0 0.3rem 0;
                        color: #718096;
                        font-size: 0.85rem;
                    }
                    .summary-hours {
                        margin: 0;
                        color: #667eea;
                        font-size: 1.4rem;
                        font-weight: 700;
                    }
                    .entries-list h4 {
                        color: #2d3748;
                        margin-bottom: 1rem;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }
                    .entry-item {
                        background: #f8f9fa;
                        border-radius: 12px;
                        padding: 1rem;
                        margin-bottom: 1rem;
                        display: flex;
                        gap: 1rem;
                        border-left: 4px solid #667eea;
                        page-break-inside: avoid;
                    }
                    .entry-number {
                        width: 40px;
                        height: 40px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-weight: bold;
                        flex-shrink: 0;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .entry-details {
                        flex: 1;
                    }
                    .entry-row {
                        display: flex;
                        justify-content: space-between;
                        padding: 0.5rem 0;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .entry-row:last-child {
                        border-bottom: none;
                    }
                    .entry-label {
                        color: #718096;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }
                    .entry-label i {
                        color: #667eea;
                    }
                    .entry-value {
                        color: #2d3748;
                        font-weight: 600;
                    }
                    .entry-duration {
                        color: #667eea;
                    }
                    @media print {
                        @page {
                            margin: 1cm;
                        }
                    }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
            </body>
            </html>
        `);

        printWindow.document.close();

        // Wait for content to load, then print
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);
        };
    };

    const handleOpenSalesAgentReport = async (agent) => {
        setSelectedSalesAgent(agent);
        setAgentDetails(null);
        setShowSalesAgentModal(true);
        try {
            setAgentDetailsLoading(true);
            const details = await settingsService.getSalesAgentDetails(agent.sales_agent_id || agent.id);
            setAgentDetails(details);
        } catch (e) {
            // silently fall back to aggregate data already in `agent`
        } finally {
            setAgentDetailsLoading(false);
        }
    };

    const handlePrintSalesAgentReport = () => {
        if (!selectedSalesAgent) return;

        const details = agentDetails || {};
        const ag = details.sales_agent || selectedSalesAgent || {};
        const sm = details.summary || {};
        const orgs = Array.isArray(details.organizations) ? details.organizations : [];

        const fmt0 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const fmt2 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const fmtDate = (dd) => dd ? new Date(dd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
        const commDisplay = (type, val) => type === 'percentage' ? `${val}%` : `${fmt0(val)} (fixed)`;
        const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const rawLogoUrl = localStorage.getItem('app_logo') || '';
        const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
        const logoUrl = (() => {
            const value = String(rawLogoUrl || '').trim();
            if (!value) return '';
            if (/^https?:\/\//i.test(value) || value.startsWith('data:image/')) return value;
            if (value.startsWith('/')) return `${apiBase}${value}`;
            return `${apiBase}/${value}`;
        })();

        const rowsPerPage = 18;
        const pages = [];
        for (let i = 0; i < orgs.length; i += rowsPerPage) {
            pages.push(orgs.slice(i, i + rowsPerPage));
        }
        if (pages.length === 0) {
            pages.push([]);
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to print this report');
            return;
        }

        const headerHtml = `
            <div class="ar-header-rt">
                <div class="ar-logo-section-rt">
                    ${logoUrl
                        ? `<img class="ar-logo-img-rt" src="${esc(logoUrl)}" alt="Logo" onerror="this.style.display='none';this.nextElementSibling.style.display='block';" /><div class="logo-fallback" style="display:none;">eHAAZRI</div>`
                        : `<div class="logo-fallback">eHAAZRI</div>`
                    }
                    <div class="ar-company-info-rt">
                        <span>Attendance Management System</span>
                        <span>support@ehaazri.com</span>
                    </div>
                </div>
                <div class="ar-meta-rt">
                    <div class="ar-report-title-rt">Sales Agent Report</div>
                    <div class="ar-report-date-rt">Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                </div>
            </div>
            <hr class="ar-div-rt" />
            <div class="ar-agent-rt">
                <div class="ar-agent-name-rt">${esc(ag.name || ag.sales_agent_name || '-')}</div>
                <div class="ar-agent-details-rt">
                    ${ag.cell_number ? `<span>Phone: ${esc(ag.cell_number)}</span>` : ''}
                    ${(ag.city || ag.country) ? `<span>Location: ${esc([ag.city, ag.country].filter(Boolean).join(', '))}</span>` : ''}
                    ${(ag.commission_type || ag.default_commission_type) ? `<span>Default commission: <strong>${esc(commDisplay(ag.commission_type ?? ag.default_commission_type, ag.commission_value ?? ag.default_commission_value))}</strong></span>` : ''}
                </div>
            </div>
            <div class="ar-kpi-grid-rt">
                <div class="ar-kpi-rt"><div class="ar-kpi-lbl">Orgs Assigned</div><div class="ar-kpi-val">${esc(sm.organizations_assigned ?? 0)}</div><div class="ar-kpi-sub">${esc(sm.active_organizations ?? 0)} active</div></div>
                <div class="ar-kpi-rt"><div class="ar-kpi-lbl">Payments</div><div class="ar-kpi-val">${esc(sm.completed_payments ?? 0)}</div><div class="ar-kpi-sub">of ${esc(sm.total_payments ?? 0)}</div></div>
                <div class="ar-kpi-rt"><div class="ar-kpi-lbl">Collected (PKR)</div><div class="ar-kpi-val">PKR ${esc(fmt0(sm.total_collected_pkr))}</div></div>
                <div class="ar-kpi-rt"><div class="ar-kpi-lbl">Collected (USD)</div><div class="ar-kpi-val">USD ${esc(fmt2(sm.total_collected_usd))}</div></div>
                <div class="ar-kpi-rt"><div class="ar-kpi-lbl">Commission (PKR)</div><div class="ar-kpi-val">PKR ${esc(fmt0(sm.total_commission_pkr))}</div></div>
                <div class="ar-kpi-rt"><div class="ar-kpi-lbl">Commission (USD)</div><div class="ar-kpi-val">USD ${esc(fmt2(sm.total_commission_usd))}</div></div>
            </div>
        `;

        const pageHtml = pages.map((chunk, pageIndex) => {
            const rows = chunk.map((item, idx) => {
                const rowNo = pageIndex * rowsPerPage + idx + 1;
                const org = item.organization ?? {};
                const m = item.metrics ?? {};
                const activeSub = item.active_subscription;
                const staffCount = activeSub?.staff_count ?? '-';
                const subEnd = activeSub?.end_date ? fmtDate(activeSub.end_date) : (activeSub ? 'No Expiry' : '-');
                const rate = commDisplay(item.commission_type, item.commission_value);
                const statusText = Number(org.status) === 2 ? 'Active' : 'Inactive';

                return `
                    <tr>
                        <td>${rowNo}</td>
                        <td><strong>${esc(org.name || '-')}</strong><br/><small>${esc(org.contact_email || '')}</small></td>
                        <td>${statusText}</td>
                        <td style="text-align:center;">${esc(staffCount)}</td>
                        <td>${esc(subEnd)}</td>
                        <td class="rt-right">${esc(m.completed_payments ?? 0)}</td>
                        <td class="rt-right">PKR ${esc(fmt0(m.total_collected_pkr))}</td>
                        <td class="rt-right">USD ${esc(fmt2(m.total_collected_usd))}</td>
                        <td class="rt-right">${esc(rate)}</td>
                        <td class="rt-right">PKR ${esc(fmt0(m.commission_pkr))}</td>
                        <td class="rt-right">USD ${esc(fmt2(m.commission_usd))}</td>
                    </tr>
                `;
            }).join('');

            const isLast = pageIndex === pages.length - 1;
            return `
                <div class="print-page">
                    ${pageIndex === 0 ? headerHtml : ''}
                    <h4 class="ar-sec-heading-rt">Organisation Breakdown</h4>
                    <table class="ar-tbl-rt">
                        <thead>
                            <tr>
                                <th>#</th><th>Organisation</th><th>Status</th><th>Staff</th><th>Sub. Ends</th>
                                <th class="rt-right">Payments</th><th class="rt-right">Collected (PKR)</th><th class="rt-right">Collected (USD)</th>
                                <th class="rt-right">Comm. Rate</th><th class="rt-right">Comm. (PKR)</th><th class="rt-right">Comm. (USD)</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rows || `<tr><td colspan="11" style="text-align:center; padding:16px;">No organisations assigned</td></tr>`}
                        </tbody>
                    </table>
                    ${isLast ? `
                        <div class="ar-grand-total-rt">
                            <div class="gt-title">Grand Total</div>
                            <div class="gt-values">
                                <span><strong>Payments:</strong> ${esc(sm.completed_payments ?? 0)}</span>
                                <span><strong>Collected PKR:</strong> PKR ${esc(fmt0(sm.total_collected_pkr))}</span>
                                <span><strong>Collected USD:</strong> USD ${esc(fmt2(sm.total_collected_usd))}</span>
                                <span><strong>Commission PKR:</strong> PKR ${esc(fmt0(sm.total_commission_pkr))}</span>
                                <span><strong>Commission USD:</strong> USD ${esc(fmt2(sm.total_commission_usd))}</span>
                            </div>
                        </div>
                    ` : ''}
                </div>
            `;
        }).join('');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Sales Agent Report - ${(selectedSalesAgent.sales_agent_name || selectedSalesAgent.name || 'Agent')}</title>
                <style>
                    @page {
                        size: A4 landscape;
                        margin: 10mm;
                    }
                    body { font-family: Arial, sans-serif; color: #1f2937; padding: 12px; }
                    .no-print, .report-actions { display: none !important; }
                    .ar-report-wrap-rt { background: #fff; }
                    .ar-header-rt {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 10px;
                    }
                    .ar-logo-section-rt { display: flex; align-items: center; gap: 10px; }
                    .ar-logo-img-rt { height: 46px; max-width: 210px; object-fit: contain; }
                    .ar-company-info-rt { display: flex; flex-direction: column; font-size: 12px; color: #475569; }
                    .ar-meta-rt { text-align: right; }
                    .ar-report-title-rt { font-size: 20px; font-weight: 700; color: #0f172a; }
                    .ar-report-date-rt { font-size: 12px; color: #64748b; margin-top: 2px; }
                    .ar-div-rt { border: none; border-top: 2px solid #e2e8f0; margin: 8px 0 12px; }
                    .ar-agent-rt { margin-bottom: 10px; }
                    .ar-agent-name-rt { font-size: 18px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px; }
                    .ar-agent-details-rt { margin-top: 4px; display: flex; gap: 14px; flex-wrap: wrap; font-size: 12px; color: #475569; }
                    .ar-badge-rt { font-size: 11px; padding: 2px 8px; border-radius: 999px; font-weight: 700; }
                    .ar-badge-rt.active { background: #dcfce7; color: #166534; }
                    .ar-badge-rt.inactive { background: #fee2e2; color: #991b1b; }
                    .ar-kpi-grid-rt { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; margin-bottom: 10px; }
                    .ar-kpi-rt { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; background: #f8fafc; }
                    .ar-kpi-lbl { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; }
                    .ar-kpi-val { font-size: 16px; font-weight: 800; color: #0f172a; margin-top: 2px; }
                    .ar-kpi-sub { font-size: 11px; color: #64748b; margin-top: 2px; }
                    .ar-table-wrap-rt { margin-top: 8px; overflow: visible !important; }
                    .ar-tbl-rt { width: 100%; border-collapse: collapse; table-layout: fixed; }
                    .ar-tbl-rt thead { display: table-header-group; }
                    .ar-tbl-rt tbody { display: table-row-group; }
                    .ar-tbl-rt tr { page-break-inside: avoid; break-inside: avoid; }
                    .ar-tbl-rt th, .ar-tbl-rt td { border: 1px solid #e5e7eb; padding: 6px 7px; font-size: 11px; text-align: left; }
                    .ar-tbl-rt th { background: #f1f5f9; font-weight: 700; color: #1e293b; }
                    .ar-grand-total-rt { margin-top: 8px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; background: #f8fafc; page-break-inside: avoid; break-inside: avoid; }
                    .ar-grand-total-rt .gt-title { font-size: 11px; font-weight: 700; color: #334155; margin-bottom: 4px; text-transform: uppercase; }
                    .ar-grand-total-rt .gt-values { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: #0f172a; }
                    .ar-foot-rt { margin-top: 10px; font-size: 10px; color: #64748b; text-align: right; }
                    .logo-fallback { font-weight: 800; font-size: 18px; color: #0f172a; }
                    .print-page { page-break-after: always; }
                    .print-page:last-child { page-break-after: auto; }
                </style>
            </head>
            <body>
                ${pageHtml}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.onload = () => {
            const logoImg = printWindow.document.querySelector('.ar-logo-img-rt');
            const safePrint = () => {
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                }, 120);
            };
            if (!logoImg) return safePrint();
            if (logoImg.complete && logoImg.naturalWidth > 0) return safePrint();
            let done = false;
            const finalize = () => {
                if (done) return;
                done = true;
                safePrint();
            };
            logoImg.onload = finalize;
            logoImg.onerror = finalize;
            setTimeout(finalize, 1200);
        };
    };

    const handleExportModalPDF = () => {
        if (!selectedRecord) return;

        const user = JSON.parse(localStorage.getItem('haazri_user') || '{}');
        const recordOrgId = selectedRecord.organization_id || selectedRecord.org_id || user?.org_id;
        const appLogo = localStorage.getItem('app_logo') || '';
        const orgLogo = recordOrgId ? (localStorage.getItem(`org_logo_${recordOrgId}`) || '') : '';
        const rawLogoUrl = orgLogo || appLogo;
        const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
        const logoUrl = (() => {
            const value = String(rawLogoUrl || '').trim();
            if (!value) return '';
            if (/^https?:\/\//i.test(value) || value.startsWith('data:image/')) return value;
            if (value.startsWith('/')) return `${apiBase}${value}`;
            return `${apiBase}/${value}`;
        })();

        const esc = (val) => String(val ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

        const organizationName = selectedRecord.organization_name || user?.org_name || '-';
        const staffName = selectedRecord.staff_name || selectedRecord.username || '-';
        const entries = (selectedRecord.entries || [])
            .slice()
            .sort((a, b) => String(a.sign_in_time || '').localeCompare(String(b.sign_in_time || '')));

        let totalMinutes = 0;
        entries.forEach((entry) => {
            const minutes = getAttendanceDurationMinutes(entry.sign_in_time, entry.sign_out_time);
            if (minutes !== null) totalMinutes += minutes;
        });
        const dayTotal = totalMinutes > 0 ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : '-';

        const rows = entries.map((entry, idx) => `
            <tr>
                <td>${idx + 1}</td>
                <td>${esc(formatTime(entry.sign_in_time))}</td>
                <td>${esc(formatTime(entry.sign_out_time))}</td>
                <td>${esc(calculateWorkingHours(entry.sign_in_time, entry.sign_out_time))}</td>
                <td>${esc(entry.attendance_type || 'Standard')}</td>
            </tr>
        `).join('');

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to export PDF');
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Attendance Details - ${esc(staffName)}</title>
                <style>
                    @page { size: A4; margin: 12mm; }
                    body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; padding: 0; }
                    .wrap { padding: 8px; }
                    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
                    .brand { display: flex; align-items: center; gap: 10px; }
                    .brand img { height: 50px; max-width: 220px; object-fit: contain; }
                    .fallback { font-size: 22px; font-weight: 800; color: #0f172a; }
                    .company { display: flex; flex-direction: column; gap: 2px; font-size: 11px; color: #475569; }
                    .company .org { font-weight: 700; font-size: 13px; color: #0f172a; }
                    .meta-top { text-align: right; }
                    .report-title { margin: 0; font-size: 20px; font-weight: 700; color: #0f172a; }
                    .report-date { margin: 3px 0 0; font-size: 12px; color: #64748b; }
                    .divider { border: 0; border-top: 2px solid #e2e8f0; margin: 6px 0 10px; }
                    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 10px; }
                    .card { border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; padding: 8px; }
                    .card .lbl { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; }
                    .card .val { font-size: 14px; font-weight: 800; margin-top: 2px; color: #0f172a; }
                    .meta { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; margin-bottom: 10px; background: #fff; }
                    .meta-row { margin: 4px 0; font-size: 12px; color: #334155; }
                    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
                    th, td { border: 1px solid #e5e7eb; padding: 7px; font-size: 11px; text-align: left; word-wrap: break-word; }
                    th { background: #f1f5f9; color: #1e293b; font-weight: 700; }
                </style>
            </head>
            <body>
                <div class="wrap">
                    <div class="header">
                        <div class="brand">
                            ${logoUrl ? `<img src="${esc(logoUrl)}" alt="Logo" onerror="this.style.display='none';this.nextElementSibling.style.display='block';" /><div class="fallback" style="display:none;">eHAAZRI</div>` : '<div class="fallback">eHAAZRI</div>'}
                            <div class="company">
                                <div class="org">${esc(organizationName)}</div>
                                <div>Attendance Management System</div>
                            </div>
                        </div>
                        <div class="meta-top">
                            <h1 class="report-title">Attendance Details Report</h1>
                            <p class="report-date">Generated: ${esc(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }))}</p>
                        </div>
                    </div>
                    <hr class="divider" />
                    <div class="summary">
                        <div class="card"><div class="lbl">Staff</div><div class="val">${esc(staffName)}</div></div>
                        <div class="card"><div class="lbl">Date</div><div class="val">${esc(formatDate(selectedRecord.date))}</div></div>
                        <div class="card"><div class="lbl">Entries</div><div class="val">${entries.length}</div></div>
                        <div class="card"><div class="lbl">Day Total</div><div class="val">${esc(dayTotal)}</div></div>
                    </div>
                    <div class="meta">
                        <div class="meta-row"><strong>Organization:</strong> ${esc(organizationName)}</div>
                        <div class="meta-row"><strong>Premises:</strong> ${esc(selectedRecord.premises_name || '-')}</div>
                        <div class="meta-row"><strong>Shift:</strong> ${esc(selectedRecord.shift_name || 'No Shift Assigned')}</div>
                    </div>
                    <table>
                        <thead>
                            <tr><th>#</th><th>Check In</th><th>Check Out</th><th>Duration</th><th>Type</th></tr>
                        </thead>
                        <tbody>
                            ${rows || '<tr><td colspan="5" style="text-align:center;">No entries</td></tr>'}
                        </tbody>
                    </table>
                </div>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        printWindow.onload = () => {
            const logoImg = printWindow.document.querySelector('.brand img');
            const safePrint = () => {
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                }, 120);
            };
            if (!logoImg) return safePrint();
            if (logoImg.complete && logoImg.naturalWidth > 0) return safePrint();
            let done = false;
            const finalize = () => {
                if (done) return;
                done = true;
                safePrint();
            };
            logoImg.onload = finalize;
            logoImg.onerror = finalize;
            setTimeout(finalize, 1200);
        };
    };

    const calculateTotalHoursForPeriod = (userId, startDate, endDate) => {
        if (!data || !data.length) return 0;

        const filteredRecords = data.filter(record => {
            if (record.user_id !== userId) return false;
            const recordDate = new Date(record.date);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return recordDate >= start && recordDate <= end;
        });

        let totalMinutes = 0;
        filteredRecords.forEach(record => {
            const minutes = getAttendanceDurationMinutes(record.sign_in_time, record.sign_out_time);
            if (minutes !== null) totalMinutes += minutes;
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return { hours, minutes, totalMinutes };
    };

    const handleViewDetails = (record) => {
        // Calculate monthly and yearly totals
        const recordDate = new Date(record.date);

        // Monthly calculation: first day to last day of the month
        const monthStart = new Date(recordDate.getFullYear(), recordDate.getMonth(), 1);
        const monthEnd = new Date(recordDate.getFullYear(), recordDate.getMonth() + 1, 0);
        const monthlyTotals = calculateTotalHoursForPeriod(record.user_id, monthStart, monthEnd);

        // Yearly calculation: Jan 1 to Dec 31
        const yearStart = new Date(recordDate.getFullYear(), 0, 1);
        const yearEnd = new Date(recordDate.getFullYear(), 11, 31);
        const yearlyTotals = calculateTotalHoursForPeriod(record.user_id, yearStart, yearEnd);

        setSelectedRecord({
            ...record,
            monthlyTotals,
            yearlyTotals,
            recordMonth: recordDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            recordYear: recordDate.getFullYear()
        });
        setShowDetailsModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = formatAttendanceTime;

    const calculateWorkingHours = (signInTime, signOutTime) => {
        if (!signInTime || !signOutTime) return '-';

        const diffMinutes = getAttendanceDurationMinutes(signInTime, signOutTime);
        if (diffMinutes === null) return 'Invalid';

        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    const getStatusBadge = (status, signIn, signOut, actualStatus) => {
        let statusClass = 'present';
        let statusText = 'Present';
        let icon = 'fa-check-circle';

        // Use actual_status from shift calculation if available
        if (status === 'holiday' || actualStatus === 'holiday') {
            statusClass = 'holiday';
            statusText = 'Holiday';
            icon = 'fa-calendar-check';
        } else if (actualStatus === 'late') {
            statusClass = 'late';
            statusText = 'Late';
            icon = 'fa-clock';
        } else if (actualStatus === 'within_margin') {
            statusClass = 'within-margin';
            statusText = 'Within Margin';
            icon = 'fa-clock';
        } else if (actualStatus === 'on_time') {
            statusClass = 'on-time';
            statusText = 'On Time';
            icon = 'fa-check-circle';
        } else if (status === 'absent' || (!signIn && !signOut)) {
            statusClass = 'absent';
            statusText = 'Absent';
            icon = 'fa-times-circle';
        } else if (status === 'late') {
            statusClass = 'late';
            statusText = 'Late';
            icon = 'fa-clock';
        } else if (status === 'on_leave') {
            statusClass = 'on-leave';
            statusText = 'On Leave';
            icon = 'fa-umbrella-beach';
        }

        return (
            <span className={`status-badge ${statusClass}`}>
                <i className={`fas ${icon}`}></i>
                {statusText}
            </span>
        );
    };

    const formatShiftTime = (timeString) => {
        if (!timeString) return '-';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const formatTimeDifference = (timeDiff) => {
        if (!timeDiff) return null;

        const isNegative = timeDiff.startsWith('-');
        const absTime = timeDiff.replace('-', '');
        const [hours, minutes] = absTime.split(':');

        const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);

        if (totalMinutes === 0) return null;

        if (isNegative) {
            return (
                <span className="time-diff early">
                    <i className="fas fa-arrow-down"></i>
                    {totalMinutes}m early
                </span>
            );
        } else {
            return (
                <span className="time-diff late">
                    <i className="fas fa-arrow-up"></i>
                    {totalMinutes}m late
                </span>
            );
        }
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisible = 5;

        // Previous button
        buttons.push(
            <button
                key="prev"
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
            >
                <i className="fas fa-chevron-left"></i>
                Previous
            </button>
        );

        // Calculate page range
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </button>
            );
        }

        // Next button
        buttons.push(
            <button
                key="next"
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
            >
                Next
                <i className="fas fa-chevron-right"></i>
            </button>
        );

        return buttons;
    };

    if (!data || data.length === 0) {
        return (
            <div className="report-table-container">
                <div className="empty-state">
                    <div className="empty-icon">
                        <i className="fas fa-clipboard-list"></i>
                    </div>
                    <h3>No Report Data Found</h3>
                    <p>No data found for the selected criteria. Try adjusting your filters.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="report-table-container">
            {reportType === 'sales-agents' && salesAgentSummary && (
                <div className="sales-agent-summary">
                    <div className="summary-card-item">
                        <div className="summary-card-title">Top Performer</div>
                        <div className="summary-card-value">{salesAgentSummary.topPerformerName}</div>
                        <div className="summary-card-sub">
                            {salesAgentSummary.topPerformerPkr > 0 && <span>₨ {salesAgentSummary.topPerformerPkr.toLocaleString(undefined, { maximumFractionDigits: 0 })} PKR</span>}
                            {salesAgentSummary.topPerformerPkr > 0 && salesAgentSummary.topPerformerUsd > 0 && <span> &nbsp;+&nbsp; </span>}
                            {salesAgentSummary.topPerformerUsd > 0 && <span>$ {salesAgentSummary.topPerformerUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</span>}
                        </div>
                    </div>
                    <div className="summary-card-item">
                        <div className="summary-card-title">Collected (Period)</div>
                        {salesAgentSummary.totalCollectedPkr > 0 && (
                            <div className="summary-card-value" style={{ fontSize: '1rem' }}>₨ {salesAgentSummary.totalCollectedPkr.toLocaleString(undefined, { maximumFractionDigits: 0 })} <small>PKR</small></div>
                        )}
                        {salesAgentSummary.totalCollectedUsd > 0 && (
                            <div className="summary-card-value" style={{ fontSize: '1rem' }}>$ {salesAgentSummary.totalCollectedUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <small>USD</small></div>
                        )}
                        {salesAgentSummary.totalCollectedPkr === 0 && salesAgentSummary.totalCollectedUsd === 0 && (
                            <div className="summary-card-value">—</div>
                        )}
                    </div>
                    <div className="summary-card-item">
                        <div className="summary-card-title">Commission (Period)</div>
                        {salesAgentSummary.totalCommPkr > 0 && (
                            <div className="summary-card-value" style={{ fontSize: '1rem' }}>₨ {salesAgentSummary.totalCommPkr.toLocaleString(undefined, { maximumFractionDigits: 0 })} <small>PKR</small></div>
                        )}
                        {salesAgentSummary.totalCommUsd > 0 && (
                            <div className="summary-card-value" style={{ fontSize: '1rem' }}>$ {salesAgentSummary.totalCommUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <small>USD</small></div>
                        )}
                        {salesAgentSummary.totalCommPkr === 0 && salesAgentSummary.totalCommUsd === 0 && (
                            <div className="summary-card-value">—</div>
                        )}
                        <div className="summary-card-sub">{salesAgentSummary.totalPayments} payments {'·'} {salesAgentSummary.totalOrgs} orgs</div>
                    </div>
                </div>
            )}

            {/* Table Header */}
            <div className="table-header">
                <div className="header-left">
                    <h3>
                        <i className="fas fa-table"></i>
                        {tableTitle}
                    </h3>
                    <p>Total Records: <strong>{groupedAttendance.length}</strong></p>
                </div>
                <div className="header-right">
                    <div className="page-size-selector">
                        <label>Show:</label>
                        <select value={pageSize} onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <button className="btn btn-export" onClick={() => onExport('csv')}>
                        <i className="fas fa-download"></i>
                        Export CSV
                    </button>
                    <button className="btn btn-export btn-pdf" onClick={handlePrintFullReport}>
                        <i className="fas fa-file-pdf"></i>
                        Export PDF
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <table className="table">
                    <thead>
                        {reportType === 'sales-agents' ? (
                            <tr>
                                <th><i className="fas fa-user-tie"></i> Sales Agent</th>
                                <th><i className="fas fa-phone"></i> Cell Number</th>
                                <th><i className="fas fa-map-marker-alt"></i> Location</th>
                                <th><i className="fas fa-building"></i> Orgs</th>
                                <th><i className="fas fa-check-circle"></i> Active</th>
                                <th><i className="fas fa-rupee-sign"></i> Collected (PKR)</th>
                                <th><i className="fas fa-dollar-sign"></i> Collected (USD)</th>
                                <th><i className="fas fa-percentage"></i> Comm. Rate</th>
                                <th><i className="fas fa-rupee-sign"></i> Comm. (PKR)</th>
                                <th><i className="fas fa-dollar-sign"></i> Comm. (USD)</th>
                                <th><i className="fas fa-receipt"></i> Payments</th>
                                <th><i className="fas fa-calendar-alt"></i> Last Payment</th>
                            </tr>
                        ) : (
                            <tr>
                                <th><i className="fas fa-user"></i> Staff Member</th>
                                <th><i className="fas fa-id-badge"></i> Designation</th>
                                <th><i className="fas fa-calendar"></i> Date</th>
                                <th><i className="fas fa-building"></i> Premises</th>
                                <th><i className="fas fa-business-time"></i> Shift</th>
                                <th><i className="fas fa-sign-in-alt"></i> TimeIN</th>
                                <th><i className="fas fa-sign-out-alt"></i> TimeOUT</th>
                                <th><i className="fas fa-hourglass-half"></i> Total Hours</th>
                                <th><i className="fas fa-info-circle"></i> Status</th>
                                <th><i className="fas fa-list"></i> Entries</th>
                                <th><i className="fas fa-cog"></i> Actions</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {reportType === 'sales-agents' ? paginatedData.map((record, idx) => {
                            const agentId = record.sales_agent_id || record.id || idx;
                            return (
                                <Fragment key={`agent-fragment-${agentId}`}>
                                    <tr key={`agent-${agentId}`} className="table-row">
                                        <td>
                                            <div className="staff-info">
                                                <div className="staff-avatar">
                                                    <i className="fas fa-user-tie"></i>
                                                </div>
                                                <div className="staff-details">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenSalesAgentReport(record)}
                                                        style={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            color: '#2563eb',
                                                            padding: 0,
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                            fontWeight: 700,
                                                            fontSize: '14px'
                                                        }}
                                                        title="Open detailed printable report"
                                                    >
                                                        {record.sales_agent_name || record.name || '-'}
                                                    </button>
                                                    <small>ID: {agentId}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{record.cell_number || '-'}</td>
                                        <td>{[record.city, record.country].filter(Boolean).join(', ') || '-'}</td>
                                        <td><strong>{record.organizations_assigned ?? 0}</strong></td>
                                        <td><strong>{record.active_organizations ?? 0}</strong></td>
                                        <td>₨ {Number(record.total_collected_pkr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                        <td>$ {Number(record.total_collected_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td>{record.commission_display || '-'}</td>
                                        <td>₨ {Number(record.commission_pkr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                        <td>$ {Number(record.commission_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td>{record.completed_payments ?? 0}</td>
                                        <td>{formatDate(record.last_payment_date)}</td>
                                    </tr>
                                </Fragment>
                            );
                        }) : paginatedData.map((record) => {
                            // Find earliest check-in time and latest check-out time
                            let earliestCheckIn = null;
                            let latestCheckOut = null;
                            let totalMinutes = 0;

                            record.entries.forEach(entry => {
                                if (entry.sign_in_time) {
                                    if (!earliestCheckIn || entry.sign_in_time < earliestCheckIn) {
                                        earliestCheckIn = entry.sign_in_time;
                                    }
                                }

                                if (entry.sign_out_time) {
                                    if (!latestCheckOut || entry.sign_out_time > latestCheckOut) {
                                        latestCheckOut = entry.sign_out_time;
                                    }
                                }

                                // Calculate total working hours for this entry
                                const minutes = getAttendanceDurationMinutes(entry.sign_in_time, entry.sign_out_time);
                                if (minutes !== null) totalMinutes += minutes;
                            });

                            const totalHours = Math.floor(totalMinutes / 60);
                            const remainingMinutes = totalMinutes % 60;
                            const totalWorkingHours = totalMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : '-';

                            return (
                                <tr key={`${record.user_id}_${record.date}`} className="table-row">
                                    <td>
                                        <div className="staff-info">
                                            <div className="staff-avatar">
                                                <i className="fas fa-user-circle"></i>
                                            </div>
                                            <div className="staff-details">
                                                <strong>{record.staff_name || record.username}</strong>
                                                <small>{record.staff_id || 'N/A'}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{record.designation || '-'}</td>
                                    <td>
                                        <div className="date-cell">
                                            <strong>{formatDate(record.date)}</strong>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="premises-badge">
                                            {record.premises_name || '-'}
                                        </span>
                                    </td>
                                    <td>
                                        {record.shift_name ? (
                                            <div className="shift-info">
                                                <div className="shift-name">
                                                    <i className="fas fa-briefcase"></i>
                                                    <strong>{record.shift_name}</strong>
                                                </div>
                                                <div className="shift-times">
                                                    <span className="shift-time">
                                                        {formatShiftTime(record.shift_start_time)} - {formatShiftTime(record.shift_end_time)}
                                                    </span>
                                                </div>
                                                {formatTimeDifference(record.time_difference)}
                                            </div>
                                        ) : (
                                            <span className="no-shift">
                                                <i className="fas fa-minus-circle"></i>
                                                No Shift Assigned
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`time-badge ${earliestCheckIn ? 'time-present' : 'time-absent'}`}>
                                            <i className={`fas ${earliestCheckIn ? 'fa-check' : 'fa-minus'}`}></i>
                                            {formatTime(earliestCheckIn)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`time-badge ${latestCheckOut ? 'time-present' : 'time-pending'}`}>
                                            <i className={`fas ${latestCheckOut ? 'fa-check' : 'fa-clock'}`}></i>
                                            {formatTime(latestCheckOut)}
                                        </span>
                                    </td>
                                    <td>
                                        <strong className="working-hours">{totalWorkingHours}</strong>
                                    </td>
                                    <td>{getStatusBadge(record.status, earliestCheckIn, latestCheckOut, record.actual_status)}</td>
                                    <td>
                                        <span className="entries-count">
                                            <i className="fas fa-list"></i>
                                            {record.entries.length} {record.entries.length === 1 ? 'Entry' : 'Entries'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="action-btn view"
                                                onClick={() => handleViewDetails(record)}
                                                title="View Details"
                                            >
                                                <i className="fas fa-eye"></i>
                                                View
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="table-footer">
                    <div className="pagination-info">
                        <i className="fas fa-info-circle"></i>
                        <span className="pagination-text">
                            {`Showing ${startIndex + 1} to ${Math.min(endIndex, groupedAttendance.length)} of ${groupedAttendance.length} records`}
                        </span>
                    </div>
                    <div className="pagination">
                        {renderPaginationButtons()}
                    </div>
                </div>
            )}

            {/* Details Modal */}
            <Modal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                title="Attendance Details"
                size="medium"
            >
                {selectedRecord && (
                    <div className="attendance-details" id="attendance-details-print">
                        {/* Action Buttons */}
                        <div className="modal-actions no-print">
                            <button className="action-btn-modal pdf" onClick={handleExportModalPDF}>
                                <i className="fas fa-file-pdf"></i>
                                Print as PDF
                            </button>
                        </div>

                        <div className="detail-header">
                            <h3>{selectedRecord.staff_name || selectedRecord.username}</h3>
                            <p className="detail-date">
                                <i className="fas fa-calendar"></i>
                                {formatDate(selectedRecord.date)}
                            </p>
                            <p className="detail-premises">
                                <i className="fas fa-building"></i>
                                {selectedRecord.premises_name}
                            </p>
                            {selectedRecord.shift_name && (
                                <p className="detail-shift">
                                    <i className="fas fa-briefcase"></i>
                                    {selectedRecord.shift_name} ({formatShiftTime(selectedRecord.shift_start_time)} - {formatShiftTime(selectedRecord.shift_end_time)})
                                </p>
                            )}
                        </div>

                        {/* Summary Statistics */}
                        <div className="summary-stats">
                            <div className="summary-card monthly">
                                <div className="summary-icon">
                                    <i className="fas fa-calendar-alt"></i>
                                </div>
                                <div className="summary-info">
                                    <h5>Monthly Total</h5>
                                    <p className="summary-period">{selectedRecord.recordMonth}</p>
                                    <p className="summary-hours">
                                        {selectedRecord.monthlyTotals.hours}h {selectedRecord.monthlyTotals.minutes}m
                                    </p>
                                </div>
                            </div>
                            <div className="summary-card yearly">
                                <div className="summary-icon">
                                    <i className="fas fa-calendar"></i>
                                </div>
                                <div className="summary-info">
                                    <h5>Yearly Total</h5>
                                    <p className="summary-period">{selectedRecord.recordYear}</p>
                                    <p className="summary-hours">
                                        {selectedRecord.yearlyTotals.hours}h {selectedRecord.yearlyTotals.minutes}m
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="entries-list">
                            <h4>
                                <i className="fas fa-list"></i>
                                Check-in/Check-out Entries ({selectedRecord.entries.length})
                            </h4>
                            {selectedRecord.entries
                                .slice()
                                .sort((a, b) => String(a.sign_in_time || '').localeCompare(String(b.sign_in_time || '')))
                                .map((entry, idx) => {
                                const workHours = calculateWorkingHours(entry.sign_in_time, entry.sign_out_time);
                                return (
                                    <div key={entry.id} className="entry-item">
                                        <div className="entry-number">#{idx + 1}</div>
                                        <div className="entry-details">
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-sign-in-alt"></i>
                                                    Check In:
                                                </span>
                                                <span className="entry-value">{formatTime(entry.sign_in_time)}</span>
                                            </div>
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-sign-out-alt"></i>
                                                    Check Out:
                                                </span>
                                                <span className="entry-value">{formatTime(entry.sign_out_time)}</span>
                                            </div>
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-hourglass-half"></i>
                                                    Duration:
                                                </span>
                                                <span className="entry-value entry-duration">{workHours}</span>
                                            </div>
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-tag"></i>
                                                    Type:
                                                </span>
                                                <span className="entry-value">
                                                    {entry.attendance_type === 'face_recognition' ? 'Face Recognition' :
                                                     entry.attendance_type === 'manual' ? 'Manual Entry' :
                                                     entry.attendance_type === 'offsite' ? 'Offsite' :
                                                     entry.attendance_type || 'Standard'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <style jsx>{`
                            .attendance-details {
                                padding: 1rem;
                            }

                            .modal-actions {
                                display: flex;
                                gap: 0.75rem;
                                justify-content: flex-end;
                                margin-bottom: 1.5rem;
                                padding-bottom: 1rem;
                                border-bottom: 2px solid #e2e8f0;
                            }

                            .action-btn-modal {
                                padding: 0.6rem 1.2rem;
                                border: none;
                                border-radius: 8px;
                                font-size: 0.9rem;
                                font-weight: 600;
                                cursor: pointer;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                                transition: all 0.3s ease;
                            }

                            .action-btn-modal.print {
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                color: white;
                            }

                            .action-btn-modal.print:hover {
                                transform: translateY(-2px);
                                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
                            }

                            .action-btn-modal.pdf {
                                background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                                color: white;
                            }

                            .action-btn-modal.pdf:hover {
                                transform: translateY(-2px);
                                box-shadow: 0 4px 12px rgba(245, 87, 108, 0.4);
                            }

                            .detail-header {
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                padding: 1.5rem;
                                border-radius: 12px;
                                color: white;
                                margin-bottom: 1.5rem;
                            }

                            .detail-header h3 {
                                margin: 0 0 0.8rem 0;
                                font-size: 1.5rem;
                            }

                            .detail-header p {
                                margin: 0.4rem 0;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                                font-size: 0.95rem;
                            }

                            .summary-stats {
                                display: grid;
                                grid-template-columns: repeat(2, 1fr);
                                gap: 1rem;
                                margin-bottom: 1.5rem;
                            }

                            .summary-card {
                                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                                border-radius: 12px;
                                padding: 1.25rem;
                                display: flex;
                                align-items: center;
                                gap: 1rem;
                                border-left: 4px solid #667eea;
                                transition: transform 0.3s ease, box-shadow 0.3s ease;
                            }

                            .summary-card:hover {
                                transform: translateY(-2px);
                                box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
                            }

                            .summary-card.monthly {
                                border-left-color: #667eea;
                            }

                            .summary-card.yearly {
                                border-left-color: #764ba2;
                            }

                            .summary-icon {
                                width: 50px;
                                height: 50px;
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                border-radius: 12px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: white;
                                font-size: 1.5rem;
                                flex-shrink: 0;
                            }

                            .summary-info {
                                flex: 1;
                            }

                            .summary-info h5 {
                                margin: 0 0 0.3rem 0;
                                color: #2d3748;
                                font-size: 0.9rem;
                                font-weight: 600;
                                text-transform: uppercase;
                                letter-spacing: 0.5px;
                            }

                            .summary-period {
                                margin: 0 0 0.3rem 0;
                                color: #718096;
                                font-size: 0.85rem;
                            }

                            .summary-hours {
                                margin: 0;
                                color: #667eea;
                                font-size: 1.4rem;
                                font-weight: 700;
                            }

                            @media (max-width: 768px) {
                                .summary-stats {
                                    grid-template-columns: 1fr;
                                }
                            }

                            .entries-list h4 {
                                color: #2d3748;
                                margin-bottom: 1rem;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                            }

                            .entry-item {
                                background: #f8f9fa;
                                border-radius: 12px;
                                padding: 1rem;
                                margin-bottom: 1rem;
                                display: flex;
                                gap: 1rem;
                                border-left: 4px solid #667eea;
                            }

                            .entry-number {
                                width: 40px;
                                height: 40px;
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: white;
                                font-weight: bold;
                                flex-shrink: 0;
                            }

                            .entry-details {
                                flex: 1;
                            }

                            .entry-row {
                                display: flex;
                                justify-content: space-between;
                                padding: 0.5rem 0;
                                border-bottom: 1px solid #e2e8f0;
                            }

                            .entry-row:last-child {
                                border-bottom: none;
                            }

                            .entry-label {
                                color: #718096;
                                font-weight: 500;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                            }

                            .entry-label i {
                                color: #667eea;
                            }

                            .entry-value {
                                color: #2d3748;
                                font-weight: 600;
                            }

                            .entry-duration {
                                color: #667eea;
                            }

                            /* Print Styles */
                            @media print {
                                .no-print,
                                .modal-actions {
                                    display: none !important;
                                }

                                .attendance-details {
                                    padding: 0;
                                    background: white;
                                }

                                .detail-header {
                                    background: #667eea !important;
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                }

                                .summary-card {
                                    background: #f8f9fa !important;
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                    page-break-inside: avoid;
                                }

                                .summary-icon {
                                    background: #667eea !important;
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                }

                                .entry-item {
                                    page-break-inside: avoid;
                                }

                                .entry-number {
                                    background: #667eea !important;
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                }

                                @page {
                                    margin: 1cm;
                                }
                            }
                        `}</style>
                    </div>
                )}
            </Modal>

            <Modal
                show={showSalesAgentModal}
                onClose={() => { setShowSalesAgentModal(false); setAgentDetails(null); }}
                title="Sales Agent Report"
                maxWidth="1280px"
            >
                {showSalesAgentModal && (() => {
                    const d = agentDetails;
                    const ag  = d?.sales_agent ?? selectedSalesAgent ?? {};
                    const sm  = d?.summary ?? {};
                    const orgs = d?.organizations ?? [];
                    const fmt0 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                    const fmt2 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const fmtDate = (dd) => dd ? new Date(dd).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
                    const commDisplay = (type, val) => type === 'percentage' ? `${val}%` : `${fmt0(val)} (fixed)`;
                    const agName = ag.name || ag.sales_agent_name || '-';
                    const agStatus = ag.status ?? ag.sales_agent_status;

                    return (
                        <div id="sales-agent-details-print">
                            {/* Toolbar */}
                            <div className="report-actions no-print">
                                <button className="action-btn-modal print" onClick={handlePrintSalesAgentReport}>
                                    <i className="fas fa-print"></i> Print / PDF
                                </button>
                            </div>

                            {agentDetailsLoading ? (
                                <div style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>Loading detailed report...</div>
                            ) : (
                            <div className="ar-report-wrap-rt">
                                {/* Header */}
                                <div className="ar-header-rt">
                                    <div className="ar-logo-section-rt">
                                        <Logo size="large" />
                                        <div className="ar-company-info-rt">
                                            <span>Attendance Management System</span>
                                            <span>support@ehaazri.com</span>
                                        </div>
                                    </div>
                                    <div className="ar-meta-rt">
                                        <div className="ar-report-title-rt">Sales Agent Report</div>
                                        <div className="ar-report-date-rt">Generated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                                    </div>
                                </div>
                                <hr className="ar-div-rt" />

                                {/* Agent info */}
                                <div className="ar-agent-rt">
                                    <div className="ar-agent-name-rt">
                                        <i className="fas fa-user-tie" style={{ color: '#0ea5e9', marginRight: '8px' }}></i>
                                        {agName}
                                        <span className={`ar-badge-rt ${Number(agStatus) === 1 ? 'active' : 'inactive'}`}>
                                            {Number(agStatus) === 1 ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="ar-agent-details-rt">
                                        {(ag.cell_number) && <span><i className="fas fa-phone"></i> {ag.cell_number}</span>}
                                        {(ag.city || ag.country) && <span><i className="fas fa-map-marker-alt"></i> {[ag.city, ag.country].filter(Boolean).join(', ')}</span>}
                                        {(ag.commission_type || ag.default_commission_type) && (
                                            <span><i className="fas fa-percent"></i> Default commission: <strong>{commDisplay(ag.commission_type ?? ag.default_commission_type, ag.commission_value ?? ag.default_commission_value)}</strong></span>
                                        )}
                                    </div>
                                </div>

                                {/* KPI strip */}
                                {d && (
                                    <>
                                    <div className="ar-kpi-grid-rt">
                                        <div className="ar-kpi-rt">
                                            <div className="ar-kpi-lbl">Orgs Assigned</div>
                                            <div className="ar-kpi-val">{sm.organizations_assigned ?? 0}</div>
                                            <div className="ar-kpi-sub">{sm.active_organizations ?? 0} active</div>
                                        </div>
                                        <div className="ar-kpi-rt">
                                            <div className="ar-kpi-lbl">Payments</div>
                                            <div className="ar-kpi-val">{sm.completed_payments ?? 0}</div>
                                            <div className="ar-kpi-sub">of {sm.total_payments ?? 0}</div>
                                        </div>
                                        <div className="ar-kpi-rt pkr">
                                            <div className="ar-kpi-lbl">Collected (PKR)</div>
                                            <div className="ar-kpi-val">PKR {fmt0(sm.total_collected_pkr)}</div>
                                        </div>
                                        <div className="ar-kpi-rt usd">
                                            <div className="ar-kpi-lbl">Collected (USD)</div>
                                            <div className="ar-kpi-val">$ {fmt2(sm.total_collected_usd)}</div>
                                        </div>
                                        <div className="ar-kpi-rt comm-pkr">
                                            <div className="ar-kpi-lbl">Commission (PKR)</div>
                                            <div className="ar-kpi-val">PKR {fmt0(sm.total_commission_pkr)}</div>
                                        </div>
                                        <div className="ar-kpi-rt comm-usd">
                                            <div className="ar-kpi-lbl">Commission (USD)</div>
                                            <div className="ar-kpi-val">$ {fmt2(sm.total_commission_usd)}</div>
                                        </div>
                                    </div>

                                    {/* Payable banner */}
                                    <div className="ar-payable-rt">
                                        <i className="fas fa-file-invoice-dollar"></i>
                                        <div>
                                            <strong>Total Payable to Agent &nbsp;</strong>
                                            <span>
                                                {Number(sm.total_commission_pkr ?? 0) > 0 && `PKR ${fmt0(sm.total_commission_pkr)} (PKR)`}
                                                {Number(sm.total_commission_pkr ?? 0) > 0 && Number(sm.total_commission_usd ?? 0) > 0 && '  +  '}
                                                {Number(sm.total_commission_usd ?? 0) > 0 && `$ ${fmt2(sm.total_commission_usd)} (USD)`}
                                                {!Number(sm.total_commission_pkr) && !Number(sm.total_commission_usd) && '- No commission earned yet'}
                                            </span>
                                        </div>
                                    </div>
                                    </>
                                )}

                                {/* Org table */}
                                <h4 className="ar-sec-heading-rt">Organisation Breakdown</h4>
                                <div className="ar-table-wrap-rt">
                                    <table className="ar-tbl-rt">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Organisation</th>
                                                <th>Status</th>
                                                <th>Staff</th>
                                                <th>Sub. Ends</th>
                                                <th className="rt-right">Payments</th>
                                                <th className="rt-right">Collected (PKR)</th>
                                                <th className="rt-right">Collected (USD)</th>
                                                <th className="rt-right">Comm. Rate</th>
                                                <th className="rt-right">Comm. (PKR)</th>
                                                <th className="rt-right">Comm. (USD)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orgs.length === 0 ? (
                                                <tr><td colSpan="11" style={{ textAlign: 'center', padding: '1.5rem', color: '#718096' }}>
                                                    {d ? 'No organisations assigned' : 'Loading...'}
                                                </td></tr>
                                            ) : orgs.map((item, idx) => {
                                                const org = item.organization ?? {};
                                                const m = item.metrics ?? {};
                                                const activeSub = item.active_subscription;
                                                const staffCount = activeSub?.staff_count ?? '-';
                                                const subEnd = activeSub?.end_date ? fmtDate(activeSub.end_date) : (activeSub ? 'No Expiry' : '-');
                                                const rate = commDisplay(item.commission_type, item.commission_value);
                                                return (
                                                    <tr key={org.id ?? idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>
                                                            <strong style={{ display: 'block' }}>{org.name}</strong>
                                                            {org.contact_email && <small style={{ color: '#718096' }}>{org.contact_email}</small>}
                                                        </td>
                                                        <td>
                                                            <span className={`ar-badge-rt ${Number(org.status) === 2 ? 'active' : 'inactive'}`}> 
                                                                {Number(org.status) === 2 ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>{staffCount}</td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>{subEnd}</td>
                                                        <td className="rt-right">{m.completed_payments ?? 0}</td>
                                                        <td className="rt-right">PKR {fmt0(m.total_collected_pkr)}</td>
                                                        <td className="rt-right">$ {fmt2(m.total_collected_usd)}</td>
                                                        <td className="rt-right">{rate}</td>
                                                        <td className="rt-right rt-comm">PKR {fmt0(m.commission_pkr)}</td>
                                                        <td className="rt-right rt-comm">$ {fmt2(m.commission_usd)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {d && orgs.length > 0 && (
                                    <div className="ar-grand-total-rt">
                                        <div className="gt-title">Grand Total</div>
                                        <div className="gt-values">
                                            <span><strong>Payments:</strong> {sm.completed_payments ?? 0}</span>
                                            <span><strong>Collected PKR:</strong> PKR {fmt0(sm.total_collected_pkr)}</span>
                                            <span><strong>Collected USD:</strong> USD {fmt2(sm.total_collected_usd)}</span>
                                            <span><strong>Commission PKR:</strong> PKR {fmt0(sm.total_commission_pkr)}</span>
                                            <span><strong>Commission USD:</strong> USD {fmt2(sm.total_commission_usd)}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="ar-notes-rt">
                                    <p>Commission is calculated per completed payment using the organisation-specific rate (if set) or the agent's default rate.</p>
                                    <p>Staff count and subscription end date reflect the current active subscription for each organisation.</p>
                                </div>
                            </div>
                            )}
                        </div>
                    );
                })()}
            </Modal>

            <style jsx>{`
        .report-table-container {
          background: white;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 2px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .sales-agent-summary {
          display: grid;
          grid-template-columns: repeat(3, minmax(180px, 1fr));
          gap: 1rem;
          padding: 1.2rem 1.5rem;
          background: #f8fbff;
          border-bottom: 1px solid #e2e8f0;
        }

        .summary-card-item {
          background: #ffffff;
          border: 1px solid #dbeafe;
          border-radius: 12px;
          padding: 0.9rem 1rem;
        }

        .summary-card-title {
          color: #64748b;
          font-size: 0.82rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          margin-bottom: 0.35rem;
        }

        .summary-card-value {
          color: #1e293b;
          font-size: 1.15rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .summary-card-sub {
          margin-top: 0.25rem;
          color: #475569;
          font-size: 0.82rem;
        }

        .sales-agent-report-modal {
          background: #fff;
        }

        .report-actions {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 0.75rem;
        }

        .sales-report-header {
          background: linear-gradient(135deg, #0f766e 0%, #1d4ed8 100%);
          color: #fff;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
        }

        .sales-report-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 700;
        }

        .sales-report-header p {
          margin: 0.3rem 0 0;
          opacity: 0.9;
          font-size: 0.9rem;
        }

        .sales-report-kpis {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .kpi-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.8rem;
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .kpi-card span {
          font-size: 0.72rem;
          text-transform: uppercase;
          color: #64748b;
          font-weight: 700;
        }

        .kpi-card strong {
          font-size: 1rem;
          color: #0f172a;
        }

        .sales-org-section h4 {
          margin: 0 0 0.75rem;
          color: #111827;
        }

        .sales-org-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
        }

        .sales-org-table th,
        .sales-org-table td {
          border: 1px solid #e2e8f0;
          padding: 0.6rem;
          font-size: 0.84rem;
          text-align: left;
        }

        .sales-org-table th {
          background: #f1f5f9;
          color: #1e293b;
          font-weight: 700;
        }

        .no-data {
          color: #64748b;
          font-size: 0.9rem;
          padding: 0.6rem 0;
        }

        .header-left h3 {
          color: #2d3748;
          margin: 0 0 0.3rem 0;
          font-size: 1.2rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .header-left h3 i {
          color: #667eea;
        }

        .header-left p {
          margin: 0;
          color: #718096;
          font-size: 0.9rem;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          flex-wrap: wrap;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #2d3748;
        }

        .page-size-selector select {
          padding: 0.5rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.9rem;
          background: white;
        }

        .btn-export {
          background: white;
          border: 2px solid #e2e8f0;
          color: #2d3748;
          padding: 0.6rem 1rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-export:hover {
          border-color: #667eea;
          color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .btn-pdf {
          border-color: #e53e3e;
          color: #e53e3e;
        }

        .btn-pdf:hover {
          border-color: #c53030;
          color: #c53030;
          background: rgba(229, 62, 62, 0.05);
        }

        .table-responsive {
          overflow-x: auto;
        }

        .table {
          width: 100%;
          border-collapse: collapse;
        }

        .table thead {
          background: #f8f9fa;
        }

        .table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .table th i {
          color: #667eea;
          margin-right: 0.5rem;
        }

        .table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9rem;
          color: #2d3748;
        }

        .table tr:hover {
          background: rgba(102, 126, 234, 0.02);
        }

        .staff-info {
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .staff-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .staff-details strong {
          display: block;
          font-weight: 600;
          color: #2d3748;
        }

        .staff-details small {
          display: block;
          color: #718096;
          font-size: 0.8rem;
        }

        .premises-badge {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .time-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.85rem;
        }

        .time-badge.time-present {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .time-badge.time-absent {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
        }

        .time-badge.time-pending {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .working-hours {
          color: #667eea;
          font-weight: 600;
          font-size: 1rem;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-badge.present {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .status-badge.absent {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .status-badge.late {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .status-badge.on-leave {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        .status-badge.holiday {
          background: rgba(139, 92, 246, 0.15);
          color: #8b5cf6;
        }

        .status-badge.on-time {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .status-badge.within-margin {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .shift-info {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          min-width: 180px;
        }

        .shift-name {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #2d3748;
          font-size: 0.9rem;
        }

        .shift-name i {
          color: #667eea;
          font-size: 0.85rem;
        }

        .shift-name strong {
          font-weight: 600;
        }

        .shift-times {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .shift-time {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .time-diff {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-top: 0.2rem;
        }

        .time-diff.late {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .time-diff.early {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .time-diff i {
          font-size: 0.7rem;
        }

        .no-shift {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
          font-style: italic;
        }

        .entries-count {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .action-btn.view {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          padding: 0.6rem 1rem;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }

        .action-btn.view:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: translateY(-2px);
        }

        .table-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          background: #f8f9fa;
          border-top: 1px solid #e2e8f0;
        }

        .pagination-info {
          color: #718096;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .pagination-info i {
          color: #667eea;
        }

        .pagination-info strong {
          display: inline;
        }

        .pagination-text {
          display: inline;
          color: #718096;
          font-weight: 500;
          white-space: nowrap;
        }

        .pagination {
          display: flex;
          gap: 0.5rem;
        }

        .pagination-btn {
          background: white;
          border: 2px solid #e2e8f0;
          color: #2d3748;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: #667eea;
          color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .pagination-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: #667eea;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #718096;
        }

        .empty-icon {
          font-size: 4rem;
          color: #667eea;
          opacity: 0.5;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          color: #2d3748;
          margin-bottom: 0.5rem;
          font-size: 1.5rem;
        }

        @media (max-width: 768px) {
          .sales-agent-summary {
            grid-template-columns: 1fr;
          }
          .sales-report-kpis {
            grid-template-columns: 1fr 1fr;
          }

          .table-header {
            flex-direction: column;
            align-items: stretch;
          }

          .header-right {
            justify-content: center;
          }

          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .table {
            min-width: 1100px;
          }

          .table-footer {
            flex-direction: column;
            gap: 1rem;
          }

          .pagination {
            justify-content: center;
          }
        }

        /* Print Styles for Main Table */
        @media print {
          .btn-export,
          .page-size-selector,
          .action-buttons,
          .table-footer,
          .no-print { display: none !important; }
          .report-table-container { box-shadow: none; }
          .table { font-size: 10pt; }
          @page { margin: 1cm; }
        }

        /* Ã¢â€â‚¬Ã¢â€â‚¬ Rich Sales Agent Report (in Reports page modal) Ã¢â€â‚¬Ã¢â€â‚¬ */
        .ar-report-wrap-rt {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12pt; color: #1a202c; background: #fff;
        }
        .ar-header-rt { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 0.9rem; }
        .ar-logo-section-rt { display: flex; align-items: center; gap: 0.75rem; }
        .ar-company-info-rt { display: flex; flex-direction: column; font-size: 9pt; color: #555; gap: 2px; }
        .ar-company-info-rt span:first-child { font-weight: 600; font-size: 10pt; color: #1a202c; }
        .ar-meta-rt { text-align: right; }
        .ar-report-title-rt { font-size: 14pt; font-weight: 700; color: #1a202c; }
        .ar-report-date-rt { font-size: 9pt; color: #718096; margin-top: 2px; }
        .ar-div-rt { border: 0; border-top: 1px solid #9a9a9a; margin: 0.8rem 0; }
        .ar-agent-rt { margin-bottom: 0.9rem; }
        .ar-agent-name-rt { font-size: 13pt; font-weight: 700; color: #1a202c; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.35rem; }
        .ar-badge-rt { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 8pt; font-weight: 700; }
        .ar-badge-rt.active { background: #c6f6d5; color: #22543d; }
        .ar-badge-rt.inactive { background: #fed7d7; color: #742a2a; }
        .ar-agent-details-rt { display: flex; flex-wrap: wrap; gap: 1rem; font-size: 10pt; color: #4a5568; }
        .ar-agent-details-rt span { display: flex; align-items: center; gap: 0.35rem; }
        .ar-agent-details-rt i { color: #a0aec0; font-size: 0.85em; }
        .ar-kpi-grid-rt { display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.5rem; margin-bottom: 0.75rem; }
        .ar-kpi-rt { padding: 0.55rem 0.7rem; border-radius: 8px; background: #f7fafc; border: 1px solid #e2e8f0; text-align: center; }
        .ar-kpi-rt.pkr { background: rgba(39,174,96,0.06); border-color: rgba(39,174,96,0.2); }
        .ar-kpi-rt.usd { background: rgba(52,152,219,0.06); border-color: rgba(52,152,219,0.2); }
        .ar-kpi-rt.comm-pkr { background: rgba(22,163,74,0.1); border-color: rgba(22,163,74,0.3); }
        .ar-kpi-rt.comm-usd { background: rgba(37,99,235,0.1); border-color: rgba(37,99,235,0.3); }
        .ar-kpi-lbl { font-size: 7pt; text-transform: uppercase; letter-spacing: 0.05em; color: #718096; font-weight: 600; margin-bottom: 0.2rem; }
        .ar-kpi-val { font-size: 11pt; font-weight: 700; color: #1a202c; line-height: 1.2; }
        .ar-kpi-rt.comm-pkr .ar-kpi-val { color: #15803d; }
        .ar-kpi-rt.comm-usd .ar-kpi-val { color: #1d4ed8; }
        .ar-kpi-sub { font-size: 7pt; color: #a0aec0; margin-top: 2px; }
        .ar-payable-rt { display: flex; align-items: center; gap: 0.7rem; padding: 0.65rem 0.9rem; border-radius: 8px; background: rgba(22,163,74,0.08); border-left: 4px solid #16a34a; margin-bottom: 1rem; font-size: 11pt; }
        .ar-payable-rt i { font-size: 1.2rem; color: #16a34a; flex-shrink: 0; }
        .ar-payable-rt strong { color: #1a202c; }
        .ar-payable-rt span { color: #15803d; font-weight: 700; }
        .ar-sec-heading-rt { font-size: 11pt; font-weight: 700; color: #2d3748; margin: 0 0 0.5rem; }
        .ar-table-wrap-rt { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 0.75rem; }
        .ar-tbl-rt { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 9pt; border-bottom: 1px solid #9a9a9a; }
        .ar-tbl-rt thead th { background: #9a9a9a; color: #fff; padding: 6px 9px; text-align: left; font-size: 8pt; font-weight: 700; border-bottom: 2px solid #555; white-space: nowrap; }
        .ar-tbl-rt tbody tr:nth-child(even) td { background: #f5f5f5; }
        .ar-tbl-rt tbody td { padding: 7px 9px; vertical-align: middle; border-bottom: 1px solid #e2e8f0; font-size: 9pt; }
        .rt-right { text-align: right !important; white-space: nowrap; }
        .rt-comm { color: #15803d !important; font-weight: 600; }
        .rt-total-row td { background: #f0fdf4 !important; border-top: 2px solid #555; font-weight: 700; padding: 8px 9px; }
        .ar-tbl-rt tfoot .rt-comm { color: #15803d !important; }
        .ar-notes-rt { font-size: 8pt; color: #718096; padding-top: 0.6rem; border-top: 1px solid #e2e8f0; }
        .ar-notes-rt p { margin: 0 0 3px; line-height: 1.5; }
        @media (max-width: 860px) { .ar-kpi-grid-rt { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 540px) { .ar-kpi-grid-rt { grid-template-columns: repeat(2, 1fr); } .ar-header-rt { flex-direction: column; } .ar-meta-rt { text-align: left; } }
      `}</style>
        </div>
    );
};

export default ReportTable;
